/**
 * Two-Step Login Page - PHASE 6
 * 
 * Step 1: Username + Password
 * Step 2: Hospital/Domain Selection (conditional based on user type)
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { completeTwoStepLogin } from '../store/authSlice';
import api from '../services/api';
import { AuthService } from '../services/auth';
import { User } from '../types/user';
import { Button } from '../components/common/Button';
import { LoginConnectionStatus } from '../components/login/LoginConnectionStatus';
import { PreLoginDisclaimerBanner } from '../components/auth/PreLoginDisclaimerBanner';
import { SessionEndedNotice } from '../components/auth/SessionEndedNotice';
import { LoginStep1Request, LoginStep1Response, LoginStep2Request, LoginStep2Response } from '../types/auth';
import { getCurrentConfig, setDocumentTitle, fetchSiteConfiguration } from '../config/siteConfig';
import { BRANDING, ONECARE_LOGO } from '../config/branding';
import { DEBUG_LOGS } from '../utils/debugFlags';

/** Allow only same-origin paths; reject protocol-relative or absolute URLs to avoid open-redirect. */
function safeRedirectPath(value: string | null): string | null {
  if (!value || typeof value !== 'string') return null;
  const s = value.trim();
  if (s === '' || !s.startsWith('/') || s.startsWith('//')) return null;
  if (/^https?:\/\//i.test(s)) return null;
  return s;
}

export const LoginPageTwoStep: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string; search?: string } })?.from;
  const redirectParam = safeRedirectPath(searchParams.get('redirect')) || safeRedirectPath(searchParams.get('next'));
  const fromPath = from?.pathname ? from.pathname + (from.search || '') : null;
  const returnUrl = redirectParam || safeRedirectPath(fromPath) || '/onecare/dashboard';
  
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  // Site configuration
  const [siteConfig, setSiteConfig] = useState(getCurrentConfig());
  
  // Two-step flow state
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [step1Data, setStep1Data] = useState<LoginStep1Response | null>(null);
  
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [hospitalCode, setHospitalCode] = useState('');
  const [selectedHospitalId, setSelectedHospitalId] = useState<number | null>(null);

  // MFA (two-factor) state for step 1
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [mfaAllowTrust, setMfaAllowTrust] = useState(false);
  const [mfaTrustHours, setMfaTrustHours] = useState(8);
  const [trustDevice, setTrustDevice] = useState(false);

  // Forced MFA enrollment at login (required admins without MFA)
  const [mfaEnroll, setMfaEnroll] = useState<{ enroll_token: string; qr_code: string; secret: string } | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollBackupCodes, setEnrollBackupCodes] = useState<string[] | null>(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isConnected = true; // Connection handled by LoginConnectionStatus component
  
  // Set document title and fetch config
  useEffect(() => {
    setDocumentTitle('Login');
    fetchSiteConfiguration().then(() => {
      setDocumentTitle('Login');
      setSiteConfig(getCurrentConfig());
    }).catch(console.error);
  }, []);

  // Prefetch CSRF token so cookie is set before user submits (avoids extra round-trip at submit time)
  useEffect(() => {
    api.get<{ csrfToken: string }>('/auth/csrf/').catch(() => {});
  }, []);
  
  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  
  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to={returnUrl} replace />;
  }
  
  // Helper function to wait for authentication cookies to be set
  const waitForAuthCookies = async (maxWait: number = 1000): Promise<boolean> => {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWait) {
      // Check for both auth cookies that Step 2 sets
      if (document.cookie.includes('hospital_auth_token') || 
          document.cookie.includes('sessionid')) {
        // Additional small delay to ensure cookies are fully propagated
        await new Promise(resolve => setTimeout(resolve, 100));
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    return false;
  };
  
  // ==================== Step 1: Username + Password ====================
  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isConnected) {
      setError('System unavailable - please wait for connection');
      return;
    }
    
    if (!username || !password) {
      setError('Please enter your username and password');
      return;
    }

    if (requiresMfa && !mfaToken) {
      setError('Please enter your authentication code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData: LoginStep1Request = {
        username,
        password,
        ...(requiresMfa ? { mfa_token: mfaToken, trust_device: trustDevice } : {}),
      };
      const response = await api.silentAuthRequest<LoginStep1Response>('/auth/login-step1/', requestData);

      // Account requires MFA but has none yet: walk them through setup now.
      if (response.requires_mfa_enrollment && response.enroll_token) {
        setMfaEnroll({
          enroll_token: response.enroll_token,
          qr_code: response.qr_code || '',
          secret: response.secret || '',
        });
        return;
      }

      // Password accepted but a two-factor code is still needed: reveal the code
      // field and let the user re-submit step 1 with it.
      if (response.requires_mfa) {
        setRequiresMfa(true);
        setMfaAllowTrust(Boolean(response.allow_trust_device));
        setMfaTrustHours(response.trust_device_hours ?? 8);
        return;
      }

      if (DEBUG_LOGS) {
        console.log('[Two-Step Login] Step 1 successful:', {
          user_type: response.user_type,
          requires_hospital_code: response.requires_hospital_code,
          available_hospitals: response.available_hospitals?.length
        });
      }

      setStep1Data(response);

      // Single-screen login: never show a hospital picker. The operational
      // hospital is defaulted (server-side) to the user's primary / first
      // accessible; superusers/org-admins switch hospital in-app afterwards.
      if (response.user_type === 'superuser') {
        await completeSuperuserLogin(response);
      } else {
        // Pass the first available hospital as the default; the backend also
        // defaults on its own when none is sent.
        await completeAutoLogin(response, response.available_hospitals?.[0]?.id);
      }

    } catch (err: any) {
      console.error('[Two-Step Login] Step 1 failed:', err);
      const data = err.response?.data;
      // A wrong 2FA code (401) — keep the code field visible so they can retry.
      if (data?.code === 'mfa_invalid') {
        setRequiresMfa(true);
        setMfaToken('');
      }
      // Prefer the backend's friendly message (lockout / attempts-remaining /
      // MFA), fall back to the short error, then a generic default.
      setError(data?.message || data?.error || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== Forced MFA enrollment at login ====================
  const handleEnrollSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaEnroll) return;
    if (enrollCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.silentAuthRequest<LoginStep1Response>('/auth/login-mfa-enroll/', {
        enroll_token: mfaEnroll.enroll_token,
        mfa_token: enrollCode,
      });
      // MFA is now enabled; the response is a step-1 success payload + codes.
      setStep1Data(response);
      setEnrollBackupCodes(response.backup_codes || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const continueAfterEnroll = async () => {
    if (!step1Data) return;
    setLoading(true);
    setError(null);
    if (step1Data.user_type === 'superuser') {
      await completeSuperuserLogin(step1Data);
    } else {
      await completeAutoLogin(step1Data, step1Data.available_hospitals?.[0]?.id);
    }
    setLoading(false);
  };

  // ==================== Auto-login for superusers ====================
  const completeSuperuserLogin = async (step1Response: LoginStep1Response) => {
    try {
      // Superuser can login with or without hospitals; send hospital_id only when available
      const firstHospital = step1Response.available_hospitals?.[0];
      const requestData: LoginStep2Request = {
        temp_token: step1Response.temp_token!,
        ...(firstHospital ? { hospital_id: firstHospital.id } : {}),
      };

      const response = await api.silentAuthRequest<LoginStep2Response>('/auth/login-step2/', requestData);

      // Wait for authentication cookies to be set before updating state
      await waitForAuthCookies();

      AuthService.persistLoginState({
        user: response.user as User,
        sessionSettings: response.session_settings,
        hospital: response.hospital ?? null,
        accessibleHospitals: response.accessible_hospitals ?? [],
      });
      
      // Update Redux state (hospital may be null when no hospitals exist)
      dispatch(completeTwoStepLogin({
        user: response.user,
        token: 'cookie',
        hospital: response.hospital ?? null,
        accessible_hospitals: response.accessible_hospitals ?? [],
        session_settings: response.session_settings
      }));

      navigate('/onecare/admin/platform');
    } catch (err: any) {
      console.error('[Two-Step Login] Superuser auto-login failed:', err);
      setError(err.response?.data?.error || 'Login failed');
      setCurrentStep(1);
    }
  };
  
  // ==================== Auto-login for single hospital users ====================
  const completeAutoLogin = async (step1Response: LoginStep1Response, hospitalId?: number) => {
    try {
      const requestData: LoginStep2Request = {
        temp_token: step1Response.temp_token!,
        ...(hospitalId ? { hospital_id: hospitalId } : {}),
      };
      
      const response = await api.silentAuthRequest<LoginStep2Response>('/auth/login-step2/', requestData);
      
      // Wait for authentication cookies to be set before updating state
      // This prevents race condition where WebSocket tries to connect before cookies are ready
      await waitForAuthCookies();
      
      AuthService.persistLoginState({
        user: response.user as User,
        sessionSettings: response.session_settings,
        hospital: response.hospital,
        accessibleHospitals: response.accessible_hospitals,
      });
      
      // Update Redux state
      dispatch(completeTwoStepLogin({
        user: response.user,
        token: 'cookie',
        hospital: response.hospital,
        accessible_hospitals: response.accessible_hospitals,
        session_settings: response.session_settings
      }));
      
      // Let the Navigate component handle redirect
      
    } catch (err: any) {
      console.error('[Two-Step Login] Auto-login failed:', err);
      setError(err.response?.data?.error || 'Login failed');
      setCurrentStep(1);
    }
  };
  
  // ==================== Step 2: Hospital Selection ====================
  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!step1Data) {
      setError('Session expired. Please login again.');
      setCurrentStep(1);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const requestData: LoginStep2Request = {
        temp_token: step1Data.temp_token!
      };
      
      // Add hospital selection based on method
      if (selectedHospitalId) {
        requestData.hospital_id = selectedHospitalId;
      } else if (hospitalCode) {
        requestData.hospital_code = hospitalCode;
      } else {
        setError('Please select a hospital or enter a hospital code');
        setLoading(false);
        return;
      }
      
      const response = await api.silentAuthRequest<LoginStep2Response>('/auth/login-step2/', requestData);
      
      // Wait for authentication cookies to be set before updating state
      // This prevents race condition where WebSocket tries to connect before cookies are ready
      await waitForAuthCookies();
      
      AuthService.persistLoginState({
        user: response.user as User,
        sessionSettings: response.session_settings,
        hospital: response.hospital,
        accessibleHospitals: response.accessible_hospitals,
      });
      
      // Update Redux state
      dispatch(completeTwoStepLogin({
        user: response.user,
        token: 'cookie',
        hospital: response.hospital,
        accessible_hospitals: response.accessible_hospitals,
        session_settings: response.session_settings
      }));
      
      // Navigate handled by isAuthenticated check
      
    } catch (err: any) {
      console.error('[Two-Step Login] Step 2 failed:', err);
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== Render ====================
  
  const inputClass =
    'w-full rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm text-white placeholder-slate-400 outline-none transition duration-200 hover:border-white/20 hover:bg-white/[0.08] focus:border-cyan-400/60 focus:bg-white/[0.09] focus:ring-2 focus:ring-cyan-400/25 disabled:opacity-50';
  const submitBtnClass =
    'w-full py-3 text-base font-bold rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-600 text-white shadow-lg shadow-cyan-500/20 hover:shadow-xl hover:shadow-cyan-500/30 hover:from-cyan-400 hover:to-indigo-500 transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none';

  return (
    <div className="relative h-full overflow-y-auto bg-[#040b1a]">
      {/* Ambient themed background (fixed + self-clipping so it never forces the page to clip/scroll) */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, #040b1a 0%, #07162b 45%, #02060d 100%)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-indigo-500/10" />
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-cyan-400/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-24 h-[28rem] w-[28rem] -translate-y-1/4 rounded-full bg-sky-500/25 blur-[130px]" />
        <div className="absolute -bottom-24 left-1/3 h-80 w-80 rounded-full bg-indigo-500/20 blur-[120px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.08),transparent_60%)]" />
        {/* faint mesh grid, faded toward the edges */}
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            WebkitMaskImage: 'radial-gradient(circle at 50% 40%, black, transparent 75%)',
            maskImage: 'radial-gradient(circle at 50% 40%, black, transparent 75%)',
          }}
        />
      </div>

      {/* Centering wrapper — centers when short, scrolls when the card is taller than the screen */}
      <div className="relative flex min-h-full items-center justify-center px-3 py-4 sm:px-6 sm:py-10">
        {/* Glass shell */}
        <div className="relative w-full max-w-5xl overflow-hidden rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.04] shadow-[0_40px_120px_-20px_rgba(2,20,40,0.85)] backdrop-blur-2xl">
          <div className="grid lg:grid-cols-2">
          {/* ============ Brand / hero panel ============ */}
          <div className="relative hidden lg:flex flex-col justify-between p-10 xl:p-12">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/15 via-transparent to-indigo-500/10" />
              <div className="absolute -top-16 -left-10 h-72 w-72 rounded-full bg-cyan-400/20 blur-[110px]" />
              <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-sky-500/20 blur-[120px]" />
              <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />
            </div>

            <div className="relative flex items-center gap-3">
              <img
                src={ONECARE_LOGO}
                alt={`${BRANDING.product.name} Logo`}
                className="h-20 w-20 object-contain"
              />
              <span className="text-4xl font-bold tracking-tight text-white">OneCare</span>
              <span className="ml-1 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-cyan-100">
                Healthcare Platform
              </span>
            </div>

            <div className="relative py-10">
              <h2 className="text-3xl xl:text-4xl font-bold leading-tight text-white">
                Unified healthcare<br />operations, in one<br />
                <span className="bg-gradient-to-r from-cyan-300 to-sky-200 bg-clip-text text-transparent">
                  secure platform.
                </span>
              </h2>
              <p className="mt-4 max-w-sm text-base leading-relaxed text-slate-300">
                Communication, AI assistance, and clinical records — connected for the teams who deliver care.
              </p>

              <ul className="mt-8 space-y-4">
                {[
                  'Real-time clinical communication',
                  'Smart Flow AI assistant',
                  'Patient registry & AI scribe',
                ].map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-slate-200">
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-cyan-400/15 ring-1 ring-cyan-300/30">
                      <svg className="h-3.5 w-3.5 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-sm font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative flex items-center gap-3 border-t border-white/10 pt-6">
              <svg className="h-5 w-5 text-cyan-300/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Encrypted &amp; audit-ready access</span>
                <span className="text-slate-600">•</span>
                <span>Powered by {BRANDING.company.name}</span>
              </div>
            </div>
          </div>

          {/* ============ Form panel ============ */}
          <div className="relative px-5 py-6 sm:p-10 lg:p-12">
            {/* Header (brand lockup shown on mobile where the side panel is hidden) */}
            <div className="mb-5 sm:mb-7">
              {/* Mobile-only centered brand lockup */}
              <div className="mb-4 flex flex-col items-center lg:hidden">
                <div className="flex items-center gap-2.5">
                  <img
                    src={ONECARE_LOGO}
                    alt={`${BRANDING.product.name} Logo`}
                    className="h-12 w-12 object-contain"
                  />
                  <span className="text-2xl font-bold tracking-tight text-white">OneCare</span>
                </div>
                <span className="mt-2.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-medium text-cyan-100">
                  Healthcare Platform
                </span>
                <div className="mt-4 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight text-white text-center lg:text-left">
                {currentStep === 1 ? 'Welcome back' : 'Select your hospital'}
              </h1>
              <p className="mt-1.5 text-sm text-slate-300 text-center lg:text-left">
                {currentStep === 1
                  ? 'Sign in to continue to your account'
                  : 'Choose your hospital to proceed'}
              </p>
            </div>

            {/* Session-ended / access notice (from ?error= on an auth redirect) */}
            <SessionEndedNotice className="mb-3 sm:mb-4" />

            {/* Confidentiality / PHI warning */}
            <div className="mb-3 sm:mb-4">
              <PreLoginDisclaimerBanner variant="dark" />
            </div>

            {/* Connection Status */}
            <div className="mb-3 sm:mb-4">
              <LoginConnectionStatus variant="dark" />
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-2.5 rounded-lg bg-red-500/10 border border-red-400/30">
                <p className="text-sm font-medium text-red-200">{error}</p>
              </div>
            )}

            {/* Step 1: Username & Password */}
            {currentStep === 1 && !mfaEnroll && !enrollBackupCodes && (
              <form onSubmit={handleStep1Submit} className="space-y-3.5 sm:space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="username" className="block text-sm font-semibold text-slate-200">
                    Username
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={inputClass}
                    placeholder="Enter your username"
                    disabled={loading || requiresMfa}
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="password" className="block text-sm font-semibold text-slate-200">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                    placeholder="Enter your password"
                    disabled={loading || requiresMfa}
                  />
                </div>

                {/* Two-factor code (shown after password is accepted) */}
                {requiresMfa && (
                  <div className="space-y-1.5">
                    <label htmlFor="mfa-token" className="block text-sm font-semibold text-slate-200">
                      {useRecoveryCode ? 'Recovery code' : 'Authentication code'}
                    </label>
                    <input
                      id="mfa-token"
                      name="mfa-token"
                      type="text"
                      inputMode={useRecoveryCode ? 'text' : 'numeric'}
                      autoComplete="one-time-code"
                      maxLength={useRecoveryCode ? 11 : 6}
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                      required
                      value={mfaToken}
                      onChange={(e) => setMfaToken(
                        useRecoveryCode
                          ? e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase()
                          : e.target.value.replace(/\D/g, '')
                      )}
                      className={`${inputClass} text-center ${useRecoveryCode ? 'tracking-widest' : 'tracking-[0.5em]'}`}
                      placeholder={useRecoveryCode ? 'xxxxx-xxxxx' : '000000'}
                      disabled={loading}
                    />
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => { setUseRecoveryCode(v => !v); setMfaToken(''); setError(null); }}
                        className="text-xs font-medium text-cyan-300 hover:text-cyan-200"
                        disabled={loading}
                      >
                        {useRecoveryCode ? 'Use authenticator app' : 'Use a recovery code'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRequiresMfa(false); setMfaToken(''); setUseRecoveryCode(false); setTrustDevice(false); setError(null); }}
                        className="text-xs font-medium text-cyan-300 hover:text-cyan-200"
                        disabled={loading}
                      >
                        Start over
                      </button>
                    </div>
                    {mfaAllowTrust && (
                      <label className="mt-1 flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={trustDevice}
                          onChange={(e) => setTrustDevice(e.target.checked)}
                          disabled={loading}
                          className="h-4 w-4 rounded border-white/20 bg-white/10 text-cyan-500 focus:ring-cyan-400/40"
                        />
                        Trust this device for {mfaTrustHours} hours (don’t ask for a code again on this browser)
                      </label>
                    )}
                  </div>
                )}

                <div className="pt-2">
                  <Button
                    type="submit"
                    disabled={loading || !isConnected}
                    className={submitBtnClass}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Authenticating...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        {requiresMfa ? 'Verify' : 'Continue'}
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Forced MFA enrollment (required admins without MFA) */}
            {currentStep === 1 && mfaEnroll && !enrollBackupCodes && (
              <form onSubmit={handleEnrollSubmit} className="space-y-4">
                <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-3">
                  <p className="text-sm font-semibold text-amber-100">Two-factor authentication required</p>
                  <p className="text-xs text-amber-200/80 mt-0.5">
                    Your account must use 2FA. Scan the code with an authenticator app
                    (Google Authenticator, Authy, Microsoft Authenticator) to finish signing in.
                  </p>
                </div>
                {mfaEnroll.qr_code && (
                  <div className="flex justify-center">
                    <img
                      src={`data:image/png;base64,${mfaEnroll.qr_code}`}
                      alt="Two-factor authentication QR code"
                      className="h-40 w-40 rounded-lg border border-white/10 bg-white p-2"
                    />
                  </div>
                )}
                {mfaEnroll.secret && (
                  <p className="text-center text-xs text-slate-400">
                    Can’t scan? Key: <span className="font-mono text-slate-200 break-all">{mfaEnroll.secret}</span>
                  </p>
                )}
                <div className="space-y-1.5">
                  <label htmlFor="enroll-code" className="block text-sm font-semibold text-slate-200">
                    Authentication code
                  </label>
                  <input
                    id="enroll-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    required
                    value={enrollCode}
                    onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))}
                    className={`${inputClass} text-center tracking-[0.5em]`}
                    placeholder="000000"
                    disabled={loading}
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => { setMfaEnroll(null); setEnrollCode(''); setError(null); }}
                    className="flex-1 px-3 py-3 rounded-xl border border-white/15 text-sm font-bold text-slate-200 hover:bg-white/5 hover:border-white/25 disabled:opacity-50 transition-all"
                    disabled={loading}
                  >
                    Back
                  </button>
                  <Button type="submit" disabled={loading || !isConnected} className={`flex-1 ${submitBtnClass}`}>
                    {loading ? 'Verifying…' : 'Verify & continue'}
                  </Button>
                </div>
              </form>
            )}

            {/* Recovery codes after forced enrollment */}
            {currentStep === 1 && enrollBackupCodes && (
              <div className="space-y-4">
                <div className="rounded-xl bg-cyan-500/10 border border-cyan-400/20 p-3">
                  <p className="text-sm font-semibold text-white">Save your recovery codes</p>
                  <p className="text-xs text-slate-300 mt-0.5">
                    Each works once if you lose access to your authenticator app. This is the
                    only time they’re shown.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3 font-mono text-sm text-slate-100 text-center">
                  {enrollBackupCodes.map((code) => (
                    <span key={code} className="tracking-wider">{code}</span>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText((enrollBackupCodes || []).join('\n'))}
                    className="flex-1 px-3 py-3 rounded-xl border border-white/15 text-sm font-bold text-slate-200 hover:bg-white/5 hover:border-white/25 disabled:opacity-50 transition-all"
                    disabled={loading}
                  >
                    Copy
                  </button>
                  <Button type="button" onClick={continueAfterEnroll} disabled={loading || !isConnected} className={`flex-1 ${submitBtnClass}`}>
                    {loading ? 'Signing in…' : 'Continue'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Hospital Selection */}
            {currentStep === 2 && step1Data && (
              <div>
                <div className="mb-5 p-3.5 rounded-xl bg-cyan-500/10 border border-cyan-400/20">
                  <p className="text-sm font-semibold text-white mb-0.5">
                    Welcome, <span className="text-cyan-200">{step1Data.first_name} {step1Data.last_name}</span>!
                  </p>
                  <p className="text-sm text-slate-300">
                    {step1Data.user_type === 'organization_admin' && 'Select which hospital to access'}
                    {step1Data.user_type === 'regular' && 'Select your hospital or enter hospital code'}
                  </p>
                </div>

                <form onSubmit={handleStep2Submit} className="space-y-3.5 sm:space-y-4">
                  {/* Hospital Dropdown (for org admins and multi-hospital users) */}
                  {step1Data.available_hospitals.length > 0 && !step1Data.requires_hospital_code && (
                    <div className="space-y-1.5">
                      <label htmlFor="hospital-select" className="block text-sm font-semibold text-slate-200">
                        Select Hospital
                      </label>
                      <select
                        id="hospital-select"
                        value={selectedHospitalId || ''}
                        onChange={(e) => setSelectedHospitalId(Number(e.target.value))}
                        className={`${inputClass} appearance-none bg-[length:1.25rem] bg-[right_0.75rem_center] bg-no-repeat pr-10 [&>option]:bg-slate-800 [&>option]:text-white`}
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")",
                        }}
                        disabled={loading}
                        required
                      >
                        <option value="">Select a hospital...</option>
                        {step1Data.available_hospitals.map((hospital) => (
                          <option key={hospital.id} value={hospital.id}>
                            {hospital.name} ({hospital.code})
                            {hospital.organization__name && ` - ${hospital.organization__name}`}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-slate-400">
                        {step1Data.available_hospitals.length} hospital(s) available
                      </p>
                    </div>
                  )}

                  {/* Hospital Code Entry (for regular users without dropdown) */}
                  {step1Data.requires_hospital_code && (
                    <div className="space-y-1.5">
                      <label htmlFor="hospital-code" className="block text-sm font-semibold text-slate-200">
                        Hospital Code
                      </label>
                      <input
                        id="hospital-code"
                        name="hospital_code"
                        type="text"
                        autoComplete="organization"
                        required
                        value={hospitalCode}
                        onChange={(e) => setHospitalCode(e.target.value.toLowerCase())}
                        className={inputClass}
                        placeholder="Enter hospital code"
                        disabled={loading}
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        Enter your facility code (e.g., "mhm")
                      </p>
                    </div>
                  )}

                  <div className="flex space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentStep(1);
                        setStep1Data(null);
                        setSelectedHospitalId(null);
                        setHospitalCode('');
                        setError(null);
                      }}
                      className="flex-1 px-3 py-3 rounded-xl border border-white/15 text-sm font-bold text-slate-200 hover:bg-white/5 hover:border-white/25 disabled:opacity-50 transition-all"
                      disabled={loading}
                    >
                      Back
                    </button>
                    <Button
                      type="submit"
                      disabled={loading || !isConnected}
                      className={`flex-1 ${submitBtnClass}`}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Logging in...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Login
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </span>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <p className="mt-5 sm:mt-8 text-center text-xs text-slate-500">
              {BRANDING.copyright.shortText} · Healthcare Services
            </p>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPageTwoStep;

